import {
  ExternalPlaidAccount,
  PageInfo,
  Query,
  TransactionEdgeR2,
} from '@varo/graphql-schema-types';
import { useCallback, useEffect, useState } from 'react';
import getExternalTransactions from 'services/graphql/queries/ExternalTransactions';
import { CombinedError, useQuery } from 'urql';

interface useExternalTransactionsProps {
  accountId: string;
  first?: number;
}

export interface externalTransactionDataProps {
  externalTransactions: Array<TransactionEdgeR2>;
  externalTransactionsLoading: boolean;
  loadingMoreData: boolean;
  refetchExternalTransactions: () => void;
  externalTransactionsError: CombinedError;
  hasNextPage: boolean;
}

const useExternalTransactions = ({
  accountId,
  first,
}: useExternalTransactionsProps) => {
  const [externalTransactions, setExternalTransactions] =
    useState<Array<TransactionEdgeR2> | null>();
  const [pageInfo, setPageInfo] = useState<PageInfo | null>();
  const [fetching, setFetching] = useState(false);

  const [{ data, fetching: loading, error }, refetch] = useQuery<Query>({
    query: getExternalTransactions,
    variables: {
      id: accountId,
      first: first,
      after: pageInfo?.endCursor,
    },
    requestPolicy: 'network-only',
  });

  const account = data?.accountR2 as ExternalPlaidAccount;

  const refresh = useCallback(() => {
    if (
      !fetching &&
      (data?.accountR2 as ExternalPlaidAccount)?.transactions.pageInfo
        .hasNextPage
    ) {
      setFetching(true);
      if (account?.transactions?.pageInfo) {
        setPageInfo(account?.transactions?.pageInfo);
      } else {
        setPageInfo(null);
      }
      refetch({
        requestPolicy: 'network-only',
        variables: {
          accountId: accountId,
          after: pageInfo?.endCursor,
        },
      });
    }
  }, [
    account?.transactions?.pageInfo,
    accountId,
    data?.accountR2,
    fetching,
    pageInfo?.endCursor,
    refetch,
  ]);

  useEffect(() => {
    const newChild = account?.transactions?.edges;

    if (!externalTransactions?.length) {
      setExternalTransactions(newChild);
    }
  }, [account?.transactions?.edges, externalTransactions?.length]);

  useEffect(() => {
    const newChildren = account?.transactions?.edges;

    if (
      externalTransactions &&
      fetching &&
      data &&
      (data?.accountR2 as ExternalPlaidAccount)?.transactions
    ) {
      setExternalTransactions([...externalTransactions, ...newChildren]);

      setPageInfo(
        (data?.accountR2 as ExternalPlaidAccount)?.transactions.pageInfo,
      );
      setFetching(false);
    }
  }, [account?.transactions?.edges, data, externalTransactions, fetching]);

  function removeDuplicateTransactions(edges: TransactionEdgeR2[]) {
    const seenIds = new Set();
    return edges?.filter((edge) => {
      const nodeId = edge.node.id;
      if (seenIds.has(nodeId)) {
        return false;
      }
      seenIds.add(nodeId);
      return true;
    });
  }

  return {
    externalTransactions:
      externalTransactions && removeDuplicateTransactions(externalTransactions),
    externalTransactionsError: error,
    externalTransactionsLoading: externalTransactions ? false : loading,
    loadingMoreData: externalTransactions ? fetching || loading : fetching,
    refetchExternalTransactions: refresh,
    hasNextPage: pageInfo?.hasNextPage,
  };
};

export default useExternalTransactions;
